import { useCallback } from 'react';

declare global {
    interface Window {
        // Module exported by reddot.js as a UMD global (undocumented - read from the script).
        // Note: window.Reddot (capital R) is only a constructor, its methods exist on instances.
        reddot?: {
            // config defaults to the one already loaded; modal = true opens the preferences dialog
            show: (config?: unknown, modal?: boolean) => boolean;
        };
    }
}

const HIDE_STYLE_ID = 'privacy-banner-hidden';
const OVERRIDE_STYLE_ID = 'privacy-banner-overrides';

// Containers rendered by reddot.js (its default elementID is "reddot")
const HIDE_CSS = '#reddot, #reddot-ide { display: none !important; }';

const CENTERED_PREFERENCES =
    '#reddot .cz-pc-wrapper:not(.cz-pc-dock-left):not(.cz-pc-dock-right):not(.cz-pc-full-screen)';

const OVERRIDE_CSS = `
/* reddot's floating gear - replaced by the gear in PrivacyFooter */
#reddot .cookie-modify-modal-notice { display: none !important; }

/* reddot's centered preferences dialog uses 15vh top padding + 90vh max-height, so tall content
   (e.g. "Show Cookies") pushes the button footer below the viewport. Keep it inside the screen
   and let only the middle part scroll. */
${CENTERED_PREFERENCES} { align-items: center !important; padding: 20px !important; }
${CENTERED_PREFERENCES} .cz-pc-container { max-height: calc(100vh - 40px) !important; margin: 0 !important; }
${CENTERED_PREFERENCES} .cz-pc-container > * { flex-shrink: 0; }
${CENTERED_PREFERENCES} .cz-pc-container > .cz-pc-content,
${CENTERED_PREFERENCES} .cz-pc-container > .cz-pc-body { flex: 1 1 auto !important; min-height: 0 !important; overflow-y: auto !important; }
`;

function addStyle(id: string, css: string) {
    if (document.getElementById(id)) {
        return;
    }

    const style = document.createElement('style');
    style.id = id;
    style.textContent = css;
    document.head.appendChild(style);
}

// Toggle a <style> that hides the consent banner rendered by the injected script
export function useHideStyle() {
    return useCallback((hidden: boolean) => {
        if (hidden) {
            addStyle(HIDE_STYLE_ID, HIDE_CSS);
        } else {
            document.getElementById(HIDE_STYLE_ID)?.remove();
        }
    }, []);
}

// Our layout fixes for reddot's UI (hide its floating gear, keep the preferences dialog on screen)
export function applyConsentStyleOverrides() {
    addStyle(OVERRIDE_STYLE_ID, OVERRIDE_CSS);
}

// Open reddot's consent banner. Before the user consents this shows the bottom notice;
// after consent reddot opens the preferences dialog instead. Returns false when reddot isn't loaded yet.
export function openConsentBanner() {
    if (typeof window.reddot?.show !== 'function') {
        console.warn('[PrivacyBanner] reddot.js is not loaded - cannot open consent banner');
        return false;
    }

    window.reddot.show(undefined, false);
    return true;
}
